import { parseISO } from "date-fns";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { z } from "zod";

import { EmptyState } from "~/components/empty-state";
import { JsonTable } from "~/components/json-table";

import type { EntityChangeRequestRow } from "~/features/entities/types";

const zChangeRequestContext = z.object({ action: z.string(), reasonForEdit: z.string().optional() });

interface EntityChangeRequestsTabProps {
  rows: EntityChangeRequestRow[];
}

export function EntityChangeRequestsTab({ rows }: EntityChangeRequestsTabProps) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <EmptyState title={t("model.changeRequest.modelNames")} description={t("feature.common.noData")} />;
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
      <table className="w-full min-w-max border-collapse text-[13px]">
        <thead>
          <tr className="bg-bg-subtle">
            <Th />
            <Th>{t("model.common.createdAt")}</Th>
            <Th>{t("model.changeRequest.operationType")}</Th>
            <Th>{t("model.changeRequest.context")}</Th>
            <Th>{t("model.changeRequest.properties")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border-default align-top last:border-b-0">
              <td className="px-3.5 py-3">
                <Link
                  to={`/change-requests/${row.changeRequestId}`}
                  aria-label={t("model.changeRequest.modelName")}
                  className="text-fg-link hover:underline"
                >
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </td>
              <td className="whitespace-nowrap px-3.5 py-3 tabular-nums text-fg-2">
                {t("date.long", { date: parseISO(row.createdAt) })}
              </td>
              <td className="whitespace-nowrap px-3.5 py-3 text-fg-1">{row.operationType}</td>
              <td className="px-3.5 py-3 text-fg-2">
                <ContextCell row={row} />
              </td>
              <td className="px-3.5 py-3">
                <div className="max-w-[36rem]">
                  <JsonTable target={row.properties} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContextCell({ row }: { row: EntityChangeRequestRow }) {
  const { t } = useTranslation();
  const parsed = zChangeRequestContext.safeParse(row.context);

  if (!parsed.success) {
    return (
      <div className="max-w-[24rem]">
        <JsonTable target={row.context} />
      </div>
    );
  }

  return (
    <div className="w-fit overflow-hidden rounded-[var(--radius-xs)] border border-border-default">
      <table className="border-collapse text-[13px]">
        <tbody>
          <ContextRow label={t("model.user.modelName")} value={row.userName} />
          <ContextRow label={t("ui.action")} value={parsed.data.action} />
          <ContextRow label={t("ui.reasonForEdit")} value={row.description} />
        </tbody>
      </table>
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-border-default last:border-b-0">
      <th className="whitespace-nowrap border-r border-border-default bg-bg-subtle px-3 py-1.5 text-left align-top font-bold text-fg-1">
        {label}
      </th>
      <td className="whitespace-pre-line px-3 py-1.5 text-fg-2">{value || "—"}</td>
    </tr>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2">
      {children}
    </th>
  );
}
